import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { io, Socket } from 'socket.io-client';
import styled, { createGlobalStyle } from 'styled-components';
import { getPublicApiUrl } from '../api';
import { useSeo } from '../hooks/useSeo';

const OverlayGlobalStyle = createGlobalStyle`
  *, *::before, *::after {
    box-sizing: border-box;
  }
  html, body, #root {
    margin: 0 !important;
    padding: 0 !important;
    width: 100% !important;
    height: 100% !important;
    min-height: 0 !important;
    overflow: hidden !important;
    background: transparent !important;
    background-color: transparent !important;
    background-image: none !important;
  }
`;

const Stage = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 6px;
  min-width: 300px;
  max-width: 400px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 11px 20px;
  border-radius: 999px;
  background: linear-gradient(135deg, #d4c4ff 0%, #b89cff 48%, #a88bf0 100%);
  border: 1px solid rgba(255, 255, 255, 0.35);
  box-shadow:
    0 4px 0 rgba(75, 45, 130, 0.35),
    0 6px 18px rgba(90, 50, 160, 0.22),
    inset 0 1px 0 rgba(255, 255, 255, 0.4);
  color: #fff;
  font-family: 'Segoe UI', system-ui, sans-serif;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-shadow: 0 1px 2px rgba(50, 20, 90, 0.25);
`;

const Name = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Qty = styled.span`
  flex-shrink: 0;
  white-space: nowrap;
  padding: 3px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.18);
  font-size: 13px;
  font-weight: 600;
`;

interface ClaimRow {
  id: string;
  displayName: string;
  quantity: number;
}

function getSocketBase() {
  return getPublicApiUrl().replace(/\/api\/?$/, '') || window.location.origin;
}

const OracleMonitorOverlayPage: React.FC = () => {
  useSeo({ title: 'Monitor oráculos', noIndex: true });

  const { monitorKey } = useParams<{ monitorKey: string }>();
  const [pending, setPending] = useState<ClaimRow[]>([]);

  useEffect(() => {
    if (!monitorKey) return;

    let socket: Socket | null = null;
    let cancelled = false;

    const fetchPending = async () => {
      try {
        const apiUrl = getPublicApiUrl();
        const { data } = await axios.get(`${apiUrl}/oracle-claims/public/${monitorKey}`);
        if (!cancelled) {
          setPending(data.pending || []);
        }
      } catch {
        if (!cancelled) setPending([]);
      }
    };

    const setup = async () => {
      await fetchPending();
      if (cancelled) return;

      socket = io(getSocketBase(), { path: '/api/socket.io' });
      socket.emit('join-oracle-monitor', { key: monitorKey });
      socket.on('oracle-monitor:update', (data: { pending?: ClaimRow[] }) => {
        setPending(data.pending || []);
      });
    };

    setup();
    const poll = window.setInterval(fetchPending, 5000);

    return () => {
      cancelled = true;
      window.clearInterval(poll);
      if (socket) {
        socket.emit('leave-oracle-monitor', { key: monitorKey });
        socket.disconnect();
      }
    };
  }, [monitorKey]);

  return (
    <>
      <OverlayGlobalStyle />
      {pending.length > 0 && (
        <Stage>
          {pending.map((c) => (
            <Row key={c.id}>
              <Name>{c.displayName}</Name>
              <Qty>
                {c.quantity} Oráculo{c.quantity !== 1 ? 's' : ''}
              </Qty>
            </Row>
          ))}
        </Stage>
      )}
    </>
  );
};

export default OracleMonitorOverlayPage;
